import React, { useEffect, useMemo, useState } from "react";
import { PageTitle } from "../../components/ui";
import { useDownloadDocument, useGetDocCategories, useGetDocuments } from "../../hooks/react-query/useDocuments";
import {
  CarDocsIcon,
  ClientsDocsIcon,
  LeftArrow,
  UsefullDocsIcon,
} from "../../icons";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import Container from "../../components/elements/container";
import { CarDocuments } from "./CarDocuments";
import {  useGetCarsForDocs } from "../../hooks/react-query/useCarData";
import { ClientDocuments } from "./ClientsDocuments";
import { LinkDocuments } from "./LinksDocuments";
import { normalizeDocumentData, validateResponse } from "./documentsUtils";

export const DocumentsPage = () => {
  const { data: docCategories, isLoading: isCategoriesLoading } = useGetDocCategories({});

  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [componentRendered, setComponentRendered] = useState("categories"); // categories, client, car, links
  const [selectedCar, setSelectedCar] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const categoryId = useMemo(()=>{
    return componentRendered === "client" 
      ? "0929bc53-fb18-4fcb-b271-5f8c596ad06e"
      : componentRendered === "car" 
        ? "8861dde3-f3d8-4413-a60a-512abb50dd1a" 
        : componentRendered === "links"
          ? "a620f307-2f4f-4ff7-94de-2f3b228da7b1" 
          : null
  }, [componentRendered])

  const enableFetch = useMemo(()=>{
    return (componentRendered === "car" && !!selectedCar && !!selectedCar?.plateno) || (["client"].includes(componentRendered))
  },[componentRendered, selectedCar])

  const { data: docs, isLoading: isDocsLoading } = useGetDocuments({categoryId:categoryId, plateno:selectedCar?.plateno, enabled:enableFetch});

  const {
    data: carList,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGetCarsForDocs(searchTerm);

  const documentCategories = [
    {
      icon: <ClientsDocsIcon className="h-20 w-20" />,
      title: "Έγγραφα πελάτη",
      description:
        "Βρες τα έγγραφα μίσθωσης, όπως συμβάσεις, όρους χρήσης, και το Quick Driver’s Guide.",
      btnText: "Περισσότερα",
      btnNav: "client",
    },
    {
      icon: <CarDocsIcon className="h-20 w-20" />,
      title: "Έγγραφα αυτοκινήτου",
      description:
        "Βρες απαραίτητα έγγραφα για το αυτοκίνητο, όπως άδεια κυκλοφορίας και ασφαλιστήριο.",
      btnText: "Περισσότερα",
      btnNav: "car",
    },
    {
      icon: <UsefullDocsIcon className="h-20 w-20" />,
      title: "Χρήσιμοι σύνδεσμοι",
      description:
        "Βρες οδηγίες και συνδέσμους για τις ψηφιακές υπηρεσίες της ΑΑΔΕ σχετικά με το αυτοκίνητο.",
      btnText: "Περισσότερα",
      btnNav: "links",
    },
  ];

  const title = () => {
    switch (componentRendered) {
      case "categories":
        return "Τα έγγραφα μου";
      case "client":
        return "Έγγραφα πελάτη";
      case "car":
        return "Έγγραφα αυτοκινήτου";
      case "links":
        return "Χρήσιμοι σύνδεσμοι";
      default:
        return "";
    }
  };

  const handleSearch = (targetValue) => {
    setSearchText(targetValue);
    setSearchTerm(targetValue);
    setSelectedCar(null);
  };

  const debouncedHandleSearch = useMemo(() => {
    return debounce(handleSearch, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  useEffect(() => {
    setSelectedCar(null)
  }, [componentRendered]);

  useEffect(()=>{validateResponse(docs)},[docs])

  return (
    <Container className="flex flex-col gap-6 max-h-[88vh] max-lg:px-0">
      <PageTitle
        displaySearch={componentRendered === "car"}
        handleSearch={(value) => {
          setSearchText(value);
          debouncedHandleSearch(value.trim());
        }}
        searchInput={searchText}
        searchClassName="max-w-[390px]"
        wrapperClassName="flex flex-row justify-between max-lg:px-5"
        title={
          <div className="flex flex-row items-center">
            <button
              className="lg:p-2"
              onClick={() => {
                if (componentRendered === "categories") navigate("/dashboard");
                if (["client", "car", "links"].includes(componentRendered)) {
                  setComponentRendered("categories");
                }
              }}
            >
              <LeftArrow width={32} height={32} />
            </button>
            <h2>{title()}</h2>
          </div>
        }
      />
      {componentRendered === "client" && <p className='text-base -mt-2'>Βρες τα έγγραφα μίσθωσης, όπως συμβάσεις, όρους χρήσης, και το Quick Driver’s Guide.</p>}
      {componentRendered === "car" && <p className='text-base -mt-2'>Βρες απαραίτητα έγγραφα για το αυτοκίνητο, όπως άδεια κυκλοφορίας και ασφαλιστήριο.</p>}
      {componentRendered === "links" && <p className='text-base -mt-2'>Βρες οδηγίες και συνδέσμους για τις ψηφιακές υπηρεσίες της ΑΑΔΕ σχετικά με το αυτοκίνητο.</p>}

      {componentRendered === "categories" && (
        <div className="grid grid-cols-3 gap-5">
          {documentCategories.map((category) => (
            <div
              key={category.title}
              className="flex w-full bg-[#FFFFFF99] rounded-[12px] p-6 flex-col justify-between min-h-[290px] lg:min-h-[314px]"
            >
              {category.icon}
              <div className="flex flex-col gap-4 pb-2">
                <h3>{category.title}</h3>
                <p className="text-body">{category.description}</p>
              </div>
              <div className="flex lg:justify-end">
                <button
                  className="bg-primary max-lg:w-full py-3 px-10 text-primary-button text-white rounded-[6px]"
                  onClick={() => setComponentRendered(category.btnNav)}
                >
                  {category.btnText}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {componentRendered === "client" && (
        <ClientDocuments 
          docs={[
            ...(normalizeDocumentData(docs) ?? []),
            ...(docCategories
              ?.filter((doc) => doc?.id === "quick_driver_guide")
              ?.map((document) => ({
                id: null,
                category: document?.id,
                name: document?.friendlyName,
                type: "Document",
                url: null,
                metadata: [],
                isExpandable: false,
              })) ?? []),
          ]}
          isCategoriesLoading={isCategoriesLoading}
          isDocumentsLoading={isDocsLoading} 
        />
      )}
      {componentRendered === "car" && (
      <CarDocuments
          carList={carList}
          selectedCar={selectedCar}
          searchTerm={searchTerm}
          handleCardClick={setSelectedCar}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          docs={[
            ...(normalizeDocumentData(docs) ?? []), 
            ...(docCategories
              ?.filter((doc) => doc?.id === "driver_confirmation")
              ?.map((document) => ({
                id: null,
                category: document?.id,
                name: document?.friendlyName,
                type: "Email",
                url: null,
                metadata: [],
                isExpandable: false,
              })) ?? []),
          ]}
          isCategoriesLoading={isCategoriesLoading}
          isDocumentsLoading={isDocsLoading}
        />
      )}
      {componentRendered === "links" && (
        <LinkDocuments 
          docs={docCategories?.filter((doc) => ["circulation_fee_payment_form","ev_free_parking_pass","inner_ring_pass"].includes(doc?.id))?.map((document) => {
            return {
              id: null,
              category: document?.id,
              name: document?.friendlyName,
              type: document?.type ?? null,
              url: null,
              metadata: [],
              isExpandable: false,
            }
          })}
          isCategoriesLoading={isCategoriesLoading}
          isDocumentsLoading={isDocsLoading} 
        />
        
      )}
    </Container>
  );
};